import type { ColumnDef } from "@tanstack/react-table";
import { LayoutTemplate, MoreHorizontal, Plus } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { ChipMenu, ListToolbar, SortMenu } from "@/components/ui/list-toolbar";
import { PageHeader } from "@/components/ui/page-header";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";

import {
  DefaultTemplate,
  DefaultTemplateStatus
} from "../../../api/types/default-template";
import { DraftType, DRAFT_TYPE_LABELS } from "../../../api/types/draft";
import { useDefaultTemplates } from "../../../contexts/DefaultTemplatesContext";
import { VISAVALUE } from "../../../types/visa.type";

type SortField = "draft_type" | "visa" | "status";
type SortOrder = "asc" | "desc";

const SORT_OPTIONS = [
  { value: "status:asc", label: "Status" },
  { value: "visa:asc", label: "Visa (A → Z)" },
  { value: "visa:desc", label: "Visa (Z → A)" },
  { value: "draft_type:asc", label: "Draft Type (A → Z)" },
  { value: "draft_type:desc", label: "Draft Type (Z → A)" }
];

const STATUS_TONE: Record<DefaultTemplateStatus, StatusTone> = {
  [DefaultTemplateStatus.PUBLISHED]: "success",
  [DefaultTemplateStatus.READY]: "info",
  [DefaultTemplateStatus.PROCESSING]: "orange",
  [DefaultTemplateStatus.FAILED]: "danger",
  [DefaultTemplateStatus.INACTIVE]: "neutral"
};

const STATUS_ORDER: Record<DefaultTemplateStatus, number> = {
  [DefaultTemplateStatus.PROCESSING]: 0,
  [DefaultTemplateStatus.PUBLISHED]: 1,
  [DefaultTemplateStatus.READY]: 2,
  [DefaultTemplateStatus.FAILED]: 3,
  [DefaultTemplateStatus.INACTIVE]: 4
};

export const TemplatesOverview: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const [selectedStatus, setSelectedStatus] = useState<
    DefaultTemplateStatus | ""
  >("");
  const [selectedVisaType, setSelectedVisaType] = useState<VISAVALUE | "">("");
  const [selectedDraftType, setSelectedDraftType] = useState<DraftType | "">(
    ""
  );
  const [selectedTemplate, setSelectedTemplate] =
    useState<DefaultTemplate | null>(null);
  const [sortField, setSortField] = useState<SortField>("status");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [isSubmittingStatusUpdate, setIsSubmittingStatusUpdate] =
    useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const {
    templates,
    isLoading,
    isInitialLoading,
    error,
    fetchTemplates,
    updateTemplateStatus
  } = useDefaultTemplates();

  useEffect(() => {
    fetchTemplates({
      status: selectedStatus || undefined,
      visa_type: selectedVisaType || undefined,
      draft_type: selectedDraftType || undefined
    });
  }, [fetchTemplates, selectedStatus, selectedVisaType, selectedDraftType]);

  const hasProcessing = useMemo(
    () => templates.some((t) => t.status === DefaultTemplateStatus.PROCESSING),
    [templates]
  );

  useEffect(() => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (hasProcessing) {
      pollingIntervalRef.current = setInterval(() => fetchTemplates(), 30000);
    }
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [hasProcessing, fetchTemplates]);

  const sortedTemplates = [...templates].sort((a, b) => {
    const multiplier = sortOrder === "asc" ? 1 : -1;
    switch (sortField) {
      case "visa":
        return multiplier * a.visa.localeCompare(b.visa);
      case "draft_type":
        return multiplier * a.draft_type.localeCompare(b.draft_type);
      case "status":
        return multiplier * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
      default:
        return 0;
    }
  });

  const STATUS_LABELS: Record<DefaultTemplateStatus, string> = {
    [DefaultTemplateStatus.PUBLISHED]: "Published",
    [DefaultTemplateStatus.READY]: "Ready",
    [DefaultTemplateStatus.PROCESSING]: "Processing",
    [DefaultTemplateStatus.FAILED]: "Failed",
    [DefaultTemplateStatus.INACTIVE]: "Inactive"
  };

  const handlePublishToggle = (template: DefaultTemplate) => {
    setSelectedTemplate(template);
    onOpen();
  };

  const confirmStatusUpdate = async () => {
    if (!selectedTemplate) return;
    setIsSubmittingStatusUpdate(true);
    try {
      const newStatus =
        selectedTemplate.status === DefaultTemplateStatus.PUBLISHED
          ? DefaultTemplateStatus.INACTIVE
          : DefaultTemplateStatus.PUBLISHED;
      await updateTemplateStatus(
        selectedTemplate.default_template_id || "",
        newStatus
      );
      toast.success(
        newStatus === DefaultTemplateStatus.PUBLISHED
          ? "Template published"
          : "Template unpublished"
      );
    } catch (error) {
      toast.error("Status update failed", {
        description:
          error instanceof Error ? error.message : "An error occurred"
      });
    } finally {
      setIsSubmittingStatusUpdate(false);
      onClose();
    }
  };

  const draftLabel = (t: string) => DRAFT_TYPE_LABELS[t as DraftType] || t;

  const columns: ColumnDef<DefaultTemplate, unknown>[] = [
    {
      id: "visa",
      header: "Visa",
      cell: ({ row }) => (
        <span className="text-base text-fg">
          {row.original.visa}
          {row.original.visa_subtype ? ` / ${row.original.visa_subtype}` : ""}
        </span>
      )
    },
    {
      id: "draft",
      header: "Draft Type",
      cell: ({ row }) => (
        <span className="text-base text-fg-2">
          {draftLabel(row.original.draft_type)}
        </span>
      )
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusBadge tone={STATUS_TONE[row.original.status]}>
          {row.original.status === DefaultTemplateStatus.PROCESSING ? (
            <Spinner />
          ) : null}
          {STATUS_LABELS[row.original.status]}
        </StatusBadge>
      )
    },
    {
      id: "published",
      header: "Published",
      cell: ({ row }) => (
        <Switch
          aria-label="Published"
          checked={row.original.status === DefaultTemplateStatus.PUBLISHED}
          onCheckedChange={() => handlePublishToggle(row.original)}
          disabled={
            row.original.status !== DefaultTemplateStatus.READY &&
            row.original.status !== DefaultTemplateStatus.PUBLISHED
          }
        />
      )
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="More options">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={
                  row.original.status === DefaultTemplateStatus.PROCESSING
                }
                onSelect={() =>
                  navigate(
                    `/admin/default-templates/${row.original.default_template_id}`
                  )
                }
              >
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  ];

  const unpublishing =
    selectedTemplate?.status === DefaultTemplateStatus.PUBLISHED;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Default Templates"
          count={isInitialLoading ? null : templates.length}
          actions={
            <Button
              size="sm"
              onClick={() => navigate("/admin/default-templates/new")}
            >
              <Plus /> New Default Template
            </Button>
          }
        />
        <ListToolbar>
          <SortMenu
            options={SORT_OPTIONS}
            value={`${sortField}:${sortOrder}`}
            onValueChange={(v) => {
              const [field, order] = v.split(":") as [SortField, SortOrder];
              setSortField(field);
              setSortOrder(order);
            }}
          />
          <ChipMenu
            label="Status"
            value={selectedStatus}
            onValueChange={(v) =>
              setSelectedStatus(v as DefaultTemplateStatus | "")
            }
            options={Object.values(DefaultTemplateStatus).map((s) => ({
              value: s,
              label: STATUS_LABELS[s]
            }))}
          />
          <ChipMenu
            label="Workflow"
            scroll
            value={selectedVisaType}
            onValueChange={(v) => setSelectedVisaType(v as VISAVALUE | "")}
            options={Object.values(VISAVALUE).map((v) => ({
              value: v,
              label: v
            }))}
          />
          <ChipMenu
            label="Draft Type"
            scroll
            value={selectedDraftType}
            onValueChange={(v) => setSelectedDraftType(v as DraftType | "")}
            options={Object.values(DraftType).map((t) => ({
              value: t,
              label: draftLabel(t)
            }))}
          />
        </ListToolbar>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {error ? (
          <EmptyState
            icon={LayoutTemplate}
            title="Couldn't Load Templates"
            description={error.message}
            className="py-16"
          />
        ) : !isInitialLoading && sortedTemplates.length === 0 ? (
          <EmptyState
            icon={LayoutTemplate}
            title="No Default Templates"
            className="py-16"
          />
        ) : (
          <DataTable
            data={sortedTemplates}
            columns={columns}
            getRowId={(t, i) => t.default_template_id || String(i)}
            isLoading={isInitialLoading}
            className="overflow-visible"
          />
        )}
      </div>

      <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {unpublishing ? "Unpublish Template" : "Publish Template"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {unpublishing
                ? "This will make the template unavailable. Continue?"
                : "This will publish the template. Continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmittingStatusUpdate}>
              Cancel
            </AlertDialogCancel>
            <Button
              size="sm"
              variant={unpublishing ? "destructive" : "primary"}
              onClick={confirmStatusUpdate}
              disabled={isSubmittingStatusUpdate}
            >
              {isSubmittingStatusUpdate ? <Spinner /> : null}
              {unpublishing ? "Unpublish" : "Publish"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
